import { EquipmentIcon } from "@xola/icons";
import { isEquipmentBased as checkIsEquipmentBased } from "common";
import { sumBy } from "lodash";
import React, { useEffect, useState } from "react";
import {
    EquipmentResource,
    Event,
    Experience,
    Permission,
    PurchaseItem,
    Resource,
    ResourceType,
    Timeslot,
} from "types";
import { useCollection } from "../../../../hooks/useCollection";
import { useResource } from "../../../../hooks/useResource";
import { useVisibility } from "../../../../hooks/useVisibility";
import { IsGranted } from "../../../Common/IsGranted";
import { NoTranslate } from "../../../Common/NoTranslate";
import { EditEventEquipmentModal, getResourceTimeslotParam } from "../../EditEventEquipmentModal";
import { ManageActionButton } from "../ManageActionButton";
import { RosterList, RosterListItem } from "../RosterList";
import { useSeller } from "@/hooks/useAccount";
import { getAggregatedResourceUsages, isPrivatePlaceholder } from "@/models/Event";

interface EventEquipmentListProps {
    event?: Event;
    timeslot?: Timeslot;
    item?: PurchaseItem;
    editable?: boolean;
    handleDashboardRoster: (eventId: ID) => void;
}

export const EventEquipmentList = ({
    event,
    timeslot,
    item,
    editable = true,
    handleDashboardRoster,
}: EventEquipmentListProps) => {
    const seller = useSeller();
    const [resourceUsages, setResourceUsages] = useState(event?.resourceUsages ?? []);

    const totalResourceCount = sumBy(getAggregatedResourceUsages(resourceUsages), "count");
    const productId = timeslot ? timeslot?.product : item?.product.id;
    const { data: experience } = useResource<Experience>(["products", productId]);

    const resourceParams = {
        event: event?.id,
        product: experience?.id,
        ...getResourceTimeslotParam(timeslot),
    };

    const hasResourceUsages = event ? event.resourceUsages.length > 0 : false;
    const fetchParams = { enabled: timeslot ? true : hasResourceUsages };

    const { data: resources = [] } = useCollection<Resource>("resources", resourceParams, fetchParams);

    const equipments = resources.filter((resource) => resource.type === ResourceType.EQUIPMENT) as EquipmentResource[];

    const [showManageEquipmentModal, { show: openManageEquipmentModal, hide: hideManageEquipmentModal }] =
        useVisibility(false);

    useEffect(() => {
        setResourceUsages(event?.resourceUsages ?? []);
    }, [event?.resourceUsages]);

    const isPrivatePlaceHolder = isPrivatePlaceholder(seller, event, timeslot);

    const handleModalClose = (timeslot?: Timeslot, newEvent?: Event) => {
        hideManageEquipmentModal();

        if (timeslot && newEvent) {
            handleDashboardRoster(newEvent.id);
        }
    };

    const isEquipmentBased = experience ? checkIsEquipmentBased(experience) : false;

    // Group by resource id and sum counts, skip zero-count entries
    const groupedResourceUsages: Record<string, { resource: { id: ID }; count: number }> = {};
    for (const usage of resourceUsages) {
        const id = usage.resource.id;
        const existing = groupedResourceUsages[id];
        if (existing) {
            existing.count += usage.count;
        } else {
            groupedResourceUsages[id] = { resource: usage.resource, count: usage.count };
        }
    }
    const aggregatedResourceUsages = Object.values(groupedResourceUsages).filter((r) => r.count > 0);

    return (
        <div>
            <RosterList
                heading="Equipment"
                count={totalResourceCount}
                icon={<EquipmentIcon />}
                className={!isEquipmentBased ? "mb-2 hover:cursor-pointer" : "mb-2"}
                onClick={!isEquipmentBased ? openManageEquipmentModal : undefined}
            >
                {aggregatedResourceUsages.map((resource) => (
                    <EquipmentItem key={resource.resource.id} resource={resource.resource} count={resource.count} />
                ))}

                {!editable && resourceUsages.length === 0 && <RosterListItem>No Equipments</RosterListItem>}
            </RosterList>
            {experience && editable && !isEquipmentBased ? (
                <IsGranted permissions={Permission.MODIFY_RESOURCES_EQUIPMENT}>
                    <ManageActionButton
                        text="Manage Equipment"
                        disabled={equipments.length === 0 || isPrivatePlaceHolder}
                        onClick={openManageEquipmentModal}
                    />
                    <EditEventEquipmentModal
                        eventId={event?.id}
                        isOpen={showManageEquipmentModal}
                        timeslot={timeslot}
                        experience={experience}
                        onClose={handleModalClose}
                    />
                </IsGranted>
            ) : null}
        </div>
    );
};

const EquipmentItem = ({ resource, count }: { resource: { id: ID }; count: number }) => {
    const { data: resourceData } = useResource<Resource>(["resources", resource.id]);
    return (
        <RosterListItem className="text-gray-darker">
            <NoTranslate>
                {resourceData?.name} (x{count})
            </NoTranslate>
        </RosterListItem>
    );
};
